import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useI18n } from '@apis/i18n';
import { ErrorRow, LoadingRow } from '@apis/ui';
import { isUnauthenticated } from '../lib/api';
import { describeError, requestIdOf } from '../lib/errors';
import { SessionContext, useMe } from '../lib/session';
import { AccessDenied, CenteredLayout, TenantPicker } from './centered';

/**
 * Auth guard for everything except /login and the public guest menu: `/v1/me` → 401
 * redirects to sign-in (keeping the target), other failures are retryable; a user with
 * several memberships and no active tenant chooses one first. Presentation only — the API
 * authorizes every request.
 */
export function SessionGate() {
  const { t } = useI18n();
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const unauthenticated = me.isError && isUnauthenticated(me.error);
  // While the lazy /login chunk loads this gate stays mounted and sees the new location;
  // redirect once, and never from /login itself (that would nest `redirect` forever).
  const redirected = useRef(false);

  useEffect(() => {
    if (!unauthenticated) {
      redirected.current = false;
      return;
    }
    if (redirected.current || location.pathname.startsWith('/login')) return;
    redirected.current = true;
    void navigate({ to: '/login', search: { redirect: location.href }, replace: true });
  }, [unauthenticated, navigate, location.href, location.pathname]);

  if (me.isPending || unauthenticated) return <LoadingRow label={t('common.loading')} />;
  if (me.isError) {
    const requestId = requestIdOf(me.error);
    return (
      <CenteredLayout>
        <ErrorRow
          message={describeError(t, me.error)}
          requestId={requestId ? t('common.requestId', { id: requestId }) : undefined}
          retryLabel={t('common.retry')}
          onRetry={() => void me.refetch()}
          retrying={me.isFetching}
        />
      </CenteredLayout>
    );
  }
  if (!me.data.active_tenant && me.data.platform_role) {
    // Platform operators need no tenant (ADR-0032): the console works without one, and a
    // platform-only account (no memberships) lands there instead of "no organization".
    if (/^\/platform(\/|$)/.test(location.pathname)) {
      return (
        <SessionContext.Provider value={me.data}>
          <Outlet />
        </SessionContext.Provider>
      );
    }
    if (me.data.memberships.length === 0) return <Navigate to="/platform" replace />;
  }
  if (!me.data.active_tenant) {
    return me.data.memberships.length > 0 ? (
      <TenantPicker me={me.data} />
    ) : (
      <AccessDenied app={t('app.name')} reason="tenant" />
    );
  }
  return (
    <SessionContext.Provider value={me.data}>
      <Outlet />
    </SessionContext.Provider>
  );
}
